import { UIState } from "@hydra/core/uiState";
import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";

export class Modal implements Plugin {
  private readonly modalTarget: string;
  private readonly modalContent: HTMLElement | null = null;
  private backdrop: HTMLElement | null = null;
  private readonly backdropClass: string = "dsc-modal-backdrop";
  private readonly closeOnBackdrop: boolean;
  private readonly closeOnEscape: boolean;
  private isOpen: boolean = false;
  private originalParent: HTMLElement | null = null;
  private originalNextSibling: HTMLElement | null = null;

  private eventListeners: Array<{
    element: Element | Document;
    event: string;
    handler: EventListener;
  }> = [];

  constructor(private readonly element: HTMLElement) {
    this.modalTarget = element.dataset.modalTarget || "";
    this.closeOnBackdrop = element.dataset.modalCloseBackdrop !== "false";
    this.closeOnEscape = element.dataset.modalCloseEscape !== "false";
    this.backdropClass = element.dataset.modalBackdropClass
      ? `dsc-modal-backdrop ${element.dataset.modalBackdropClass}`
      : this.backdropClass;

    if (this.modalTarget) {
      this.modalContent = document.querySelector(
        `[data-modal="${this.modalTarget}"]`,
      );
    } else {
      this.modalContent = element.querySelector("[data-modal]");
    }
  }

  init(): void {
    if (!this.modalContent) {
      console.warn("Modal content not found for modal trigger:", this.element);
      return;
    }

    this.setupModalContent();
    this.setupTrigger();
    this.setupCloseListeners();
    this.setupGlobalListeners();
  }

  private setupModalContent(): void {
    if (!this.modalContent) return;

    this.modalContent.classList.add("hidden");
    this.modalContent.setAttribute("aria-hidden", "true");
    this.modalContent.setAttribute("role", "dialog");
    this.modalContent.setAttribute("aria-modal", "true");
  }

  private setupTrigger(): void {
    const handler = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      this.show();
    };

    this.addEventListener(this.element, "click", handler);
  }

  private setupCloseListeners(): void {
    if (!this.modalContent) return;

    const closeButtons =
      this.modalContent.querySelectorAll("[data-modal-close]");
    closeButtons.forEach((button) => {
      const handler = (e: Event) => {
        e.preventDefault();
        e.stopPropagation();
        this.hide();
      };
      this.addEventListener(button, "click", handler);
    });
  }

  private setupGlobalListeners(): void {
    if (this.closeOnEscape) {
      const keydownHandler = (e: Event) => {
        const keyboardEvent = e as KeyboardEvent;
        if (keyboardEvent.key === "Escape" && this.isOpen) {
          this.hide();
        }
      };
      this.addEventListener(document, "keydown", keydownHandler);
    }
  }

  private addEventListener(
    element: Element | Document,
    event: string,
    handler: EventListener,
  ): void {
    element.addEventListener(event, handler);
    this.eventListeners.push({ element, event, handler });
  }

  private removeAllEventListeners(): void {
    this.eventListeners.forEach(({ element, event, handler }) => {
      element.removeEventListener(event, handler);
    });
    this.eventListeners = [];
  }

  /**
   * Favorites se mueve a `body`; no hereda clases del header. En vista móvil
   * (`UIState.isSmallDevice`, alineado con header.ts) añade modificadores
   * `dsc-modal-content--viewport-fill` / `dsc-modal-backdrop--viewport-fill`
   * (solo con `--favorites`; ver modal.css).
   */
  private syncFavoritesViewportFill(active: boolean): void {
    if (this.modalTarget !== "favorites") return;
    const shouldFill = active && UIState.getInstance().isSmallDevice;
    this.modalContent?.classList.toggle(
      "dsc-modal-content--viewport-fill",
      shouldFill,
    );
    this.backdrop?.classList.toggle(
      "dsc-modal-backdrop--viewport-fill",
      shouldFill,
    );
  }

  private createBackdrop(): void {
    this.backdrop = document.createElement("div");
    this.backdrop.className = this.backdropClass;
    this.backdrop.setAttribute("aria-hidden", "true");

    if (this.closeOnBackdrop) {
      const handler = (e: Event) => {
        if (e.target === this.backdrop) {
          this.hide();
        }
      };
      this.addEventListener(this.backdrop, "click", handler);
    }

    document.body.appendChild(this.backdrop);

    this.backdrop.classList.add("opacity-100");
  }

  private removeBackdrop(): void {
    if (!this.backdrop) return;

    this.backdrop.classList.remove("opacity-100");
    this.backdrop.classList.add("opacity-0");

    setTimeout(() => {
      if (this.backdrop?.parentNode) {
        this.backdrop.parentNode.removeChild(this.backdrop);
      }
      this.backdrop = null;
    }, 300);
  }

  show(): void {
    if (this.isOpen || !this.modalContent) return;

    this.isOpen = true;

    this.originalParent = this.modalContent.parentElement;
    this.originalNextSibling = this.modalContent
      .nextElementSibling as HTMLElement;

    this.createBackdrop();

    document.body.appendChild(this.modalContent);

    this.syncFavoritesViewportFill(true);

    this.modalContent.classList.remove("hidden");
    this.modalContent.classList.add("block");
    this.modalContent.setAttribute("aria-hidden", "false");

    this.modalContent.classList.add("opacity-100", "scale-100");
    this.modalContent.classList.remove("opacity-0", "scale-95");

    document.body.classList.add("overflow-hidden");

    if (this.modalContent.tabIndex === -1) {
      this.modalContent.tabIndex = -1;
    }
    this.modalContent.focus();

    this.handleVideoPlay();

    this.element.dispatchEvent(
      new CustomEvent("modal:show", {
        detail: { modal: this, content: this.modalContent },
        bubbles: true,
      }),
    );
  }

  hide(): void {
    if (!this.isOpen || !this.modalContent) return;

    this.syncFavoritesViewportFill(false);

    this.isOpen = false;

    this.handleVideoPause();

    this.modalContent.classList.remove("opacity-100", "scale-100");
    this.modalContent.classList.add("opacity-0", "scale-95");

    setTimeout(() => {
      if (this.modalContent) {
        this.modalContent.classList.add("hidden");
        this.modalContent.classList.remove("block");
        this.modalContent.setAttribute("aria-hidden", "true");

        if (this.originalParent) {
          if (this.originalNextSibling) {
            this.originalParent.insertBefore(
              this.modalContent,
              this.originalNextSibling,
            );
          } else {
            this.originalParent.appendChild(this.modalContent);
          }
        }

        this.originalParent = null;
        this.originalNextSibling = null;
      }
    }, 300);

    this.removeBackdrop();

    document.body.classList.remove("overflow-hidden");

    this.element.dispatchEvent(
      new CustomEvent("modal:hide", {
        detail: { modal: this, content: this.modalContent },
        bubbles: true,
      }),
    );
  }

  toggle(): void {
    if (this.isOpen) {
      this.hide();
    } else {
      this.show();
    }
  }

  isVisible(): boolean {
    return this.isOpen;
  }

  private handleVideoPlay(): void {
    if (!this.modalContent) return;

    const videos = this.modalContent.querySelectorAll("video");
    const iframes = this.modalContent.querySelectorAll("iframe");

    videos.forEach((video) => {
      video.currentTime = 0;
      video.play().catch((error) => {
        console.warn("Failed to auto-play video:", error);
      });
    });

    iframes.forEach((iframe) => {
      const src = iframe.src;
      if (src?.includes("youtube.com")) {
        try {
          const url = new URL(src);
          url.searchParams.set("autoplay", "1");
          iframe.src = url.toString();
        } catch (error) {
          console.warn("Error handling iframe autoplay:", error);
        }
      }
    });
  }

  private handleVideoPause(): void {
    if (!this.modalContent) return;

    const videos = this.modalContent.querySelectorAll("video");
    const iframes = this.modalContent.querySelectorAll("iframe");

    videos.forEach((video) => {
      try {
        video.pause();
        video.currentTime = 0;
      } catch (error) {
        console.warn("Error pausing video:", error);
      }
    });

    iframes.forEach((iframe) => {
      const src = iframe.src;
      if (src?.includes("youtube.com")) {
        try {
          const url = new URL(src);
          url.searchParams.delete("autoplay");
          iframe.src = url.toString();
        } catch (error) {
          console.warn("Error stopping iframe playback:", error);
        }
      }
    });
  }

  destroy(): void {
    if (this.isOpen) {
      this.hide();
    }

    this.removeAllEventListeners();
  }
}

PluginRegistry.register("modal", Modal);
